import { JarvisProduct } from '@vfde-sails/avc-core';
import { FootnotePlaceholder, ProductPlaceholder } from '../../enums/placeholder';
import { getCMSConfig } from '../cms';
import {
    PlaceholderMap,
    convertToPriceString,
    formatDeviceNamesText,
    formatReadableList,
    getLowerTariffSpeeds,
    replaceAllPlaceholders,
} from '../formatter';
import { getPriceWithRegioCharge } from '../regioCharge';

export const getFootnoteCmsContent = () => {
    const { headlineText, sections } = getCMSConfig()?.footnoteTariffItem || {};

    if (!headlineText || !sections) {
        return undefined;
    }

    return {
        headline: headlineText,
        content: Object.values(sections).join(''),
    };
};

const getFootnoteRegioChargeHeadlineText = (regioCharge: number = 0) => {
    return regioCharge ? `+ ${convertToPriceString(regioCharge)} € Regio` : '';
};

const getFootnoteRegioChargeText = (regioCharge: number = 0) => {
    const { regioFeeDetail } = getCMSConfig()?.footnoteTariffItem || {};

    if (!regioCharge || !regioFeeDetail) {
        return '';
    }

    return regioFeeDetail
        .replace(FootnotePlaceholder.REGIO_FEE_PRICE, convertToPriceString(regioCharge));
};

const getFootnoteStairwayText = (product: JarvisProduct, regioCharge?: number) => {
    const { gross, month } = product.discount;
    const { discountDetail } = getCMSConfig()?.footnoteTariffItem || {};

    if (!month || !discountDetail) {
        return '';
    }

    const priceNumber = getPriceWithRegioCharge(gross, regioCharge);

    return replaceAllPlaceholders(discountDetail, {
        [FootnotePlaceholder.DISCOUNT_DURATION]: month.toString(),
        [FootnotePlaceholder.DISCOUNT_PRICE]: convertToPriceString(priceNumber),
    });
};

const getChangeToLowerSection = (product: JarvisProduct, tariffs: JarvisProduct[]) => {
    const lowerSpeeds = getLowerTariffSpeeds(tariffs, product.download);

    if (!lowerSpeeds.length) {
        return '';
    }

    const { headline, content } = getCMSConfig()?.footnoteTariffItem?.wechselgarantieDetail || {};
    const { month } = product.discount;

    if (!month || !headline || !content) {
        return '';
    }

    return replaceAllPlaceholders(`${headline}${content}`, {
        [FootnotePlaceholder.DISCOUNT_DURATION_END]: (1 + month).toString(),
        [FootnotePlaceholder.AVAILABLE_TARIFFS]: formatReadableList(lowerSpeeds, 'oder'),
        [FootnotePlaceholder.NEW_CONTRACT_PERIOD]: (+product.duration - month).toString(),
    });
};

export const getFootnoteHeadlineMapper = (product: JarvisProduct, regioCharge?: number): PlaceholderMap => ({
    [FootnotePlaceholder.HEADLINE_BANDWIDTH]: product.download,
    [FootnotePlaceholder.HEADLINE_REGIO_FEE]: getFootnoteRegioChargeHeadlineText(regioCharge),
});

const getFinalPrice = (product: JarvisProduct, regioCharge?: number) => convertToPriceString(
    getPriceWithRegioCharge(+product.price.monthly, regioCharge)
);

export const getFootnoteContentMapper = (
    product: JarvisProduct,
    tariffs: JarvisProduct[],
    regioCharge?: number,
): PlaceholderMap => ({
    [FootnotePlaceholder.PRODUCT_NAME]: product.name,
    [FootnotePlaceholder.DISCOUNT_TEXT]: getFootnoteStairwayText(product, regioCharge),
    [FootnotePlaceholder.FINAL_PRICE]: getFinalPrice(product, regioCharge),
    [FootnotePlaceholder.REGIO_FEE_TEXT]: getFootnoteRegioChargeText(regioCharge),
    [ProductPlaceholder.PROVISION_FEE]: (undefined !== product.provisionFee?.price)
        ? convertToPriceString(product.provisionFee.price) : '',
    [ProductPlaceholder.DURATION]: product.duration,
    [FootnotePlaceholder.PRODUCT_DEVICES]: formatDeviceNamesText(product),
    [FootnotePlaceholder.WECHSEL_SECTION]: getChangeToLowerSection(product, tariffs),
});
